import { translationPacket } from 'common/actions';
import { HAS_ACCESS } from 'common/config/constants';
import { Epg, LiveChannels } from 'common/constants/data-types';
import {
  ChannelSettings,
  INITIAL_STATE,
  LANG_DEFAULT,
  LOCK_PERIODS,
  ProfileSettings,
  RESOLUTION_AUTO,
  Settings,
  SUBTITLES_OFF
} from 'common/reducers/profileSettings';
import { minutesToMS } from 'common/services/helpers';
import { checkLockStatusForChannel, checkVisibleStatusForChannel } from './helpers';

/**
 * Stripe (set as default values) settings that we don't want to store in the backend
 * @param profileSettings
 */
export const stripUnwantedProfileSettings = (profileSettings: ProfileSettings): ProfileSettings => {
  return {
    ...profileSettings,
    react_tv_settings: {
      ...profileSettings.react_tv_settings,
      channels: Object.keys(profileSettings.react_tv_settings.channels).reduce((acc, channelId) => {
        const channelSettings = profileSettings.react_tv_settings.channels[channelId];
        acc[channelId] = {
          ...channelSettings,
          // Strip out any unwanted settings that we don't want to store in the backend
          audio: null,
          subtitles: null,
          resolution: null
          //
        };
        return acc;
      }, {} as ChannelSettings)
    }
  };
};

const legacyOff = (v: any) => v === -1 || v === '-1'; // legacy "off"/"auto" sentinel
const isUnset = (v: any) => v == null || v === '';

/* normalizeReactTVSettings rewrites legacy stored values to the canonical
 * LangPref/Resolution strings so the rest of the app only ever sees those.
 * Global audio/subtitles/resolution fall back to "und"/"und"/"auto"; per-channel
 * keeps null = "inherit the global value". */
export function normalizeReactTVSettings(settings: Settings): Settings {
  const gSub = (v: any) => (legacyOff(v) ? SUBTITLES_OFF : isUnset(v) ? LANG_DEFAULT : String(v));
  const gAud = (v: any) => (isUnset(v) || legacyOff(v) ? LANG_DEFAULT : String(v));
  const gRes = (v: any) => (legacyOff(v) || isUnset(v) ? RESOLUTION_AUTO : String(v));
  const cSub = (v: any) => (isUnset(v) ? null : legacyOff(v) ? SUBTITLES_OFF : String(v));
  const cAud = (v: any) => (isUnset(v) ? null : legacyOff(v) ? LANG_DEFAULT : String(v));
  const cRes = (v: any) => (isUnset(v) ? null : legacyOff(v) ? RESOLUTION_AUTO : String(v));

  const channels: ChannelSettings = {};
  Object.keys(settings.channels || {}).forEach((id) => {
    const c = settings.channels[id];
    channels[id] = {
      ...c,
      audio: cAud(c.audio),
      subtitles: cSub(c.subtitles),
      resolution: cRes(c.resolution)
    };
  });

  return {
    ...settings,
    audio: gAud(settings.audio),
    subtitles: gSub(settings.subtitles),
    resolution: gRes(settings.resolution),
    channels
  };
}

const isProfileSettings = (
  object: ChannelSettings | ProfileSettings
): object is ProfileSettings => {
  return 'react_tv_settings' in object;
};
export function generateChannelSettings(
  settings: ChannelSettings | ProfileSettings,
  liveChannels: LiveChannels
) {
  const channels = isProfileSettings(settings) ? settings.react_tv_settings?.channels : settings;

  // Sort channels by their effective position, then assign contiguous 1..N
  // Manually reordered channels (savedPosition !== originalPosition) take priority
  // over default-positioned ones when there's a tie
  const sorted = Object.keys(liveChannels)
    .map((channelId) => {
      const savedPosition = channels?.[channelId]?.position;
      const originalPosition = liveChannels[channelId].originalPosition;
      const isManuallyReordered = savedPosition != null && savedPosition !== originalPosition;

      return {
        channelId,
        effectivePosition: savedPosition ?? originalPosition,
        originalPosition,
        isManuallyReordered
      };
    })
    .sort((a, b) => {
      if (a.effectivePosition !== b.effectivePosition) {
        return a.effectivePosition - b.effectivePosition;
      }
      // Manually reordered channels win ties
      if (a.isManuallyReordered !== b.isManuallyReordered) {
        return a.isManuallyReordered ? -1 : 1;
      }
      return a.originalPosition - b.originalPosition;
    });

  const positionMap: Record<string, number> = {};
  sorted.forEach((item, index) => {
    positionMap[item.channelId] = index + 1;
  });

  return Object.keys(liveChannels).reduce((acc, channelId) => {
    const channelSettings = channels?.[channelId] || {};
    const liveChannel = liveChannels[channelId];

    acc[channelId] = {
      ...channelSettings,
      favoritePosition: channelSettings.favoritePosition || 0,
      visible: channelSettings.visible || true,
      audio: channelSettings.audio || null,
      resolution: channelSettings.resolution || null,
      subtitles: channelSettings.subtitles || null,
      position: positionMap[channelId],
      locked: channelSettings.locked || liveChannel.locked || false
    };
    return acc;
  }, {});
}

export function generateDefaultSettings(liveChannels: LiveChannels): Settings {
  const channelSettings = Object.keys(liveChannels).reduce((acc, channelId) => {
    const liveChannel = liveChannels[channelId];
    acc[channelId] = {
      favoritePosition: 0,
      favorite: false,
      visible: true,
      audio: null,
      resolution: null,
      subtitles: null,
      position: liveChannel.originalPosition,
      locked: liveChannel.locked || false
    };
    return acc;
  }, {});
  return {
    ...INITIAL_STATE.react_tv_settings,
    lockPeriod: minutesToMS(5),
    channels: { ...channelSettings }
  };
}

export const propagateChannelAccessToEpg = (epg: Epg, profileSettings: ProfileSettings) => ({
  ...epg,
  visible:
    epg.access === HAS_ACCESS &&
    checkVisibleStatusForChannel(profileSettings, epg.chanId) &&
    !checkLockStatusForChannel(profileSettings, epg.chanId)
});

export const getLockPeriodValues = () => {
  return [
    LOCK_PERIODS.ALWAYS_LOCK,
    minutesToMS(5),
    minutesToMS(10),
    minutesToMS(20),
    minutesToMS(30),
    minutesToMS(60),
    LOCK_PERIODS.SESSION_LOCK
  ];
};

export function getLockPeriods(i18n: typeof translationPacket) {
  const { minutes, immediately, untilRestart } = i18n.mainSettings;
  const minutesLabel = minutes.toLowerCase();

  const lockPeriodsLabels = [
    immediately,
    `5 ${minutesLabel}`,
    `10 ${minutesLabel}`,
    `20 ${minutesLabel}`,
    `30 ${minutesLabel}`,
    `60 ${minutesLabel}`,
    untilRestart
  ];

  return lockPeriodsLabels.map((label, index) => ({
    label,
    value: getLockPeriodValues()[index]
  }));
}
